import { useState } from 'react'
import type { ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import type {
  ShipmentDetail,
  ShipmentStatus,
} from '#/shared/api/generated/shipment/models'
import { useDuplicateShipmentApiV1ShipmentsShipmentIdDuplicatePost } from '#/shared/api/generated/shipment/theCargoShipment'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { useCopyLink } from '#/shared/lib/clipboard'
import { toast } from '#/shared/ui/sonner'
import {
  ActivityHistoryDialog,
  useActivityHistory,
} from '#/widgets/activity-history-dialog'
import {
  DetailToolbar,
  DetailToolbarCompact,
  MORE_MENU_ACTION,
} from '#/widgets/detail-toolbar'
import type {
  ArchiveSubmenuConfig,
  ExtraMenuItem,
  MarkAsConfig,
  MoreMenuAction,
} from '#/widgets/detail-toolbar'
import type { UserOption } from '#/shared/ui/user-dropdown'
import { formatShipmentStatus } from '../lib/format'
import { invalidateShipmentLists } from '../lib/invalidate-shipment-queries'
import {
  ORDER_MARK_AS_STATUSES,
  orderMarkAsLabelKey,
} from '../lib/order-statuses'
import {
  QUOTE_MARK_AS_STATUSES,
  quoteMarkAsLabelKey,
} from '../lib/quote-statuses'
import type { QuoteMarkAsStatus } from '../lib/quote-statuses'
import { SHIPMENT_LINK_KEY, getShipmentLink } from '../lib/shipment-links'
import { STAGE_ROUTE } from '../lib/stage-route'
import { useBackToQuote, useCanBackToQuote } from '../lib/use-back-to-quote'
import { useMarkShipmentStatus } from '../lib/use-mark-shipment-status'
import { SHIPMENT_STAGES } from '../model/stages'
import type { ShipmentStage } from '../model/stages'
import {
  ReassignConfirmDialog,
  useReassignFlow,
  useShipmentUserOptions,
} from './shipment-owner-dropdown'

function useMarkAsConfig(
  shipment: ShipmentDetail | undefined,
  stage: ShipmentStage | undefined,
): MarkAsConfig | undefined {
  const { t } = useTranslation()
  const { markAs } = useMarkShipmentStatus()

  if (stage === SHIPMENT_STAGES.LEAD) return undefined
  const isQuote = stage === SHIPMENT_STAGES.QUOTE
  const options = isQuote
    ? QUOTE_MARK_AS_STATUSES.map((status) => ({
        value: status,
        label: t(quoteMarkAsLabelKey(status)),
      }))
    : ORDER_MARK_AS_STATUSES.map((status) => ({
        value: status,
        label: t(orderMarkAsLabelKey(status)),
      }))
  return {
    label: t('detailToolbar.moreOptions.markAs'),
    currentStatus: shipment?.status ?? undefined,
    options,
    onSelect: (status: string) => {
      if (!shipment?.id) return
      markAs(shipment.id, status as ShipmentStatus)
    },
  }
}

/** "Back to Quotes" status submenu — reverts a New Order to the quote stage,
 *  landing on the picked quote status. Hidden unless the shipment is eligible
 *  and the user holds the required grants. */
function useBackToQuoteConfig(
  shipment: ShipmentDetail | undefined,
): MarkAsConfig | undefined {
  const { t } = useTranslation()
  const eligible = useCanBackToQuote(shipment)
  const { backToQuote } = useBackToQuote()
  if (!eligible) return undefined
  return {
    label: t('detailToolbar.moreOptions.backToQuotes'),
    options: QUOTE_MARK_AS_STATUSES.map((status) => ({
      value: status,
      label: t(quoteMarkAsLabelKey(status)),
    })),
    onSelect: (status: string) => {
      if (!shipment?.id) return
      backToQuote(shipment.id, status as QuoteMarkAsStatus)
    },
  }
}

/** Stage-appropriate "Copy … link" menu entries from the API-provided
 *  `shipment.links` — an absent key means the link doesn't apply. */
function useCopyLinkMenuItems(
  shipment: ShipmentDetail | undefined,
): ExtraMenuItem[] {
  const { t } = useTranslation()
  const copyLink = useCopyLink()
  const items: ExtraMenuItem[] = []
  const booking = getShipmentLink(
    shipment?.links,
    SHIPMENT_LINK_KEY.onlineBooking,
  )
  const tracking = getShipmentLink(shipment?.links, SHIPMENT_LINK_KEY.tracking)
  if (booking) {
    items.push({
      key: 'copyBookingLink',
      label: t('detailToolbar.moreOptions.copyBookingLink'),
      onSelect: () => void copyLink(booking),
    })
  }
  if (tracking) {
    items.push({
      key: 'copyTrackingLink',
      label: t('detailToolbar.moreOptions.copyTrackingLink'),
      onSelect: () => void copyLink(tracking),
    })
  }
  return items
}

function useDuplicateShipment(
  shipmentId: string | undefined,
  stage: ShipmentStage | undefined,
) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const mutation = useDuplicateShipmentApiV1ShipmentsShipmentIdDuplicatePost({
    mutation: {
      onSuccess: (response) => {
        invalidateShipmentLists(queryClient)
        const newId = response.data.id
        const route = stage ? STAGE_ROUTE[stage] : undefined
        toast.success(t('detailToolbar.duplicateSuccess'), {
          action: route
            ? {
                label: t('detailToolbar.duplicateView'),
                onClick: () =>
                  navigate({
                    to: route.to,
                    params: { [route.paramKey]: newId } as never,
                  }),
              }
            : undefined,
        })
      },
      onError: (error) => {
        toast.error(
          getApiErrorDetail(error) ?? t('detailToolbar.duplicateError'),
        )
      },
    },
  })

  return () => {
    if (!shipmentId || mutation.isPending) return
    mutation.mutate({ shipmentId, data: null })
  }
}

interface ShipmentDetailToolbarProps {
  shipment?: ShipmentDetail
  stage?: ShipmentStage
  compact?: boolean
  showUser?: boolean
  onEdit?: () => void
  onMinimize?: () => void
  onClose?: () => void
  onPrev?: () => void
  onNext?: () => void
  onMoreAction?: (action: MoreMenuAction) => void
  onUserChange?: (user: UserOption) => void
  primaryAction?: ReactNode
  archive?: ArchiveSubmenuConfig
  requests?: ReactNode
}

export function ShipmentDetailToolbar({
  shipment,
  stage,
  compact,
  showUser,
  onEdit,
  onMinimize,
  onClose,
  onPrev,
  onNext,
  onMoreAction,
  onUserChange,
  primaryAction,
  archive,
  requests,
}: ShipmentDetailToolbarProps) {
  const { users, currentUser } = useShipmentUserOptions(shipment)

  const reassign = useReassignFlow(shipment, currentUser.id, onUserChange)

  const [historyOpen, setHistoryOpen] = useState(false)
  const history = useActivityHistory(shipment?.id, { enabled: historyOpen })
  const duplicate = useDuplicateShipment(shipment?.id, stage)
  const markAs = useMarkAsConfig(shipment, stage)
  const backToQuote = useBackToQuoteConfig(shipment)
  const extraMenuItems = useCopyLinkMenuItems(shipment)

  const handleMoreAction = (action: MoreMenuAction) => {
    if (action === MORE_MENU_ACTION.activityHistory) {
      setHistoryOpen(true)
      return
    }
    duplicate()
    onMoreAction?.(action)
  }

  const commonProps = {
    user: currentUser,
    users,
    onUserChange: reassign.requestReassign,
    onMoreAction: handleMoreAction,
    extraMenuItems,
    markAs,
    backToQuote,
    archive,
    primaryAction,
    requests,
  }

  return (
    <>
      {compact ? (
        <DetailToolbarCompact
          {...commonProps}
          showUser={showUser}
          onEdit={onEdit}
        />
      ) : (
        <DetailToolbar
          {...commonProps}
          id={shipment?.code ?? ''}
          status={formatShipmentStatus(shipment?.status)}
          onMinimize={onMinimize}
          onClose={onClose}
          onPrev={onPrev}
          onNext={onNext}
        />
      )}
      <ToolbarDialogs
        historyOpen={historyOpen}
        onHistoryOpenChange={setHistoryOpen}
        history={history}
        reassign={reassign}
      />
    </>
  )
}

function ToolbarDialogs({
  historyOpen,
  onHistoryOpenChange,
  history,
  reassign,
}: {
  historyOpen: boolean
  onHistoryOpenChange: (open: boolean) => void
  history: ReturnType<typeof useActivityHistory>
  reassign: ReturnType<typeof useReassignFlow>
}) {
  return (
    <>
      <ActivityHistoryDialog
        open={historyOpen}
        onOpenChange={onHistoryOpenChange}
        entries={history.entries}
        totalCount={history.totalCount}
        isLoading={history.isLoading}
        hasMore={history.hasMore}
        isLoadingMore={history.isLoadingMore}
        onLoadMore={history.loadMore}
        onExportCsv={history.exportCsv}
      />
      <ReassignConfirmDialog
        target={reassign.pendingReassign}
        onCancel={reassign.cancelReassign}
        onConfirm={reassign.confirmReassign}
      />
    </>
  )
}
